/**
 * زمان‌ها و مکان‌های یک رخداد، وقتی بیشتر از یکی است (مایگریشن 0054) —
 * قرینه‌ی apps/mobile/src/features/discover/OccurrenceList.tsx.
 *
 * ۹۰٪ رخدادها هیچ سطری اینجا ندارند و این کامپوننت اصلاً رندر نمی‌شود؛ همان
 * یک تاریخ و یک مکانِ بالای صفحه کافی است. ولی نمایشگاهی که سی‌وشش سانس دارد
 * با یک تاریخ توصیف نمی‌شود.
 *
 * هر سطر **لینکِ خودش** را دارد. این نکته‌ی اصلیِ کل ماجراست: هر سانس صفحه‌ی
 * فروشِ خودش را دارد و نگه داشتنِ یکی‌شان یعنی دور ریختنِ راهِ خریدِ بقیه.
 * مکان فقط وقتی نوشته می‌شود که با سطرِ قبلی فرق داشته باشد — یک ستونِ
 * تکراری از یک اسمِ ثابت فقط سطر را شلوغ می‌کند.
 */
import React from 'react';
import { formatDateTime } from '@/core/lib/dates';
import { t } from '@/core/lib/i18n';
import { domainOf } from '@/core/lib/url';
import { withUtm } from '@/core/lib/outboundUrl';
import type { EventOccurrence } from '@/core/domain/types';

/** اسمی که برای مکانِ یک اجرا نوشته می‌شود. */
const placeOf = (o: EventOccurrence): string => o.venueName || o.address || '';

/** همان قاعده‌ی همیشگی: پروتکل و www و اسلشِ آخر لینک را دو تا نمی‌کنند. */
const normUrl = (u?: string | null) => (u ?? '').trim().toLowerCase()
  .replace(/^https?:\/\/(www\.)?/, '').replace(/\/+$/, '');

/**
 * لینک، به‌صورتِ دامنه.
 *
 * یک `<a>`ِ واقعی می‌ماند و نه دکمه — «باز کردن در تبِ تازه» و «کپیِ نشانی» و
 * کلیکِ وسط باید کار کنند. شمارش فقط یک کارِ جانبیِ کنارِ همان کلیک است، پس نه
 * `preventDefault` دارد نه انتظارِ جواب (همان قاعده‌ی لینکِ منبع در ItemPage).
 */
function OccLink({ url, onClick }: { url: string; onClick?: () => void }) {
  return (
    <a className="occ-link" href={withUtm(url)} target="_blank" rel="noreferrer noopener"
      title={url} onClick={onClick}>
      {domainOf(url) || url}
    </a>
  );
}

export function OccurrenceList({ rows, onLinkClick }: {
  rows: EventOccurrence[];
  /** کلیک روی لینکِ یک سطر — برای شمارشِ محبوبیت. */
  onLinkClick?: () => void;
}) {
  if (rows.length === 0) return null;

  /**
   * لینک‌ها وقتی همه یکی‌اند، یک بار نوشته می‌شوند.
   *
   * دو سانسِ یک سالن معمولاً یک صفحه‌ی فروش دارند؛ تکرارِ همان نشانی در هر
   * سطر فقط جدول را شلوغ می‌کند و این سؤال را می‌سازد که «فرقی داشتند که
   * ندیدم؟». ولی جایی که واقعاً فرق دارند — دو مکانِ متفاوت، هر کدام با
   * صفحه‌ی خودش — هر سطر لینکِ خودش را نشان می‌دهد، چون همان نکته‌ی اصلیِ
   * نگه داشتنِ هر دوست.
   */
  const links = rows.map((r) => normUrl(r.url)).filter(Boolean);
  const shared = links.length === rows.length && new Set(links).size === 1
    ? rows[0].url ?? '' : '';

  return (
    <div className="occlist">
      {shared ? (
        <div className="occ-shared">
          <b className="field-label">{t('eventOccurrencesSameLink')}</b>
          <OccLink url={shared} onClick={onLinkClick} />
        </div>
      ) : null}
      {rows.map((o, i) => {
        const place = placeOf(o);
        const samePlace = i > 0 && place === placeOf(rows[i - 1]);
        return (
          <div className="occ-row" key={o.id}>
            <div className="occ-body">
              {/* عنوانِ خاصِ این اجرا اول می‌آید، چون وقتی هست (بازیِ دو تیم)
                  مهم‌ترین چیزِ سطر است. */}
              {o.title ? <b className="occ-title">{o.title}</b> : null}
              <span className="occ-when">
                {o.startsAt ? formatDateTime(o.startsAt) : t('adminEmptyValue')}
              </span>
              {place && !samePlace ? <span className="occ-place">{place}</span> : null}
            </div>
            {o.url && !shared ? <OccLink url={o.url} onClick={onLinkClick} /> : null}
          </div>
        );
      })}
    </div>
  );
}
